import React, { useMemo, useState } from 'react'
import { Empty, Input, Segmented, Select, Switch } from 'antd'
import {
  AppstoreOutlined,
  DownOutlined,
  SearchOutlined,
  ToolOutlined,
  AlertOutlined,
  CarOutlined,
  BarChartOutlined,
  TeamOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons'
import { Permission, PermissionValue, getGroupedPermissions, isPermissionVisible } from '../utils'
import { RoleSettingsSection } from './RoleSettingsSection'

interface PermissionsEditorProps {
  permissions: Permission[]
  currentPermissions: PermissionValue[]
  onPermissionChange: (permissions: PermissionValue[]) => void
}

export const PermissionsEditor = ({
  permissions,
  currentPermissions,
  onPermissionChange,
}: PermissionsEditorProps) => {
  const [initialOrder] = useState(() => {
    const getValue = (id: number) => currentPermissions.find(item => item.id === id)?.value ?? null
    return getGroupedPermissions(permissions)
      .sort((a, b) => {
        const enabled = (items: Permission[]) =>
          items.some(
            item =>
              getValue(item.id) !== null &&
              getValue(item.id) !== false &&
              isPermissionVisible(item, permissions, getValue),
          )
        return Number(enabled(b.permissions)) - Number(enabled(a.permissions))
      })
      .map(group => group.groupId)
  })
  const [initiallyExpanded] = useState(() => {
    const activeGroups = getGroupedPermissions(permissions).filter(group =>
      group.permissions.some(permission =>
        currentPermissions.some(
          value => value.id === permission.id && value.value !== null && value.value !== false,
        ),
      ),
    )
    return new Set(
      (activeGroups.length ? activeGroups.map(group => group.groupId) : initialOrder).slice(0, 2),
    )
  })
  const [search, setSearch] = useState('')
  const [onlyEnabled, setOnlyEnabled] = useState(false)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const values = useMemo(
    () => new Map(currentPermissions.map(item => [item.id, item.value])),
    [currentPermissions],
  )
  const getValue = (id: number) => values.get(id) ?? null
  const active = (id: number) =>
    getValue(id) !== null && getValue(id) !== false && getValue(id) !== ''
  const query = search.trim().toLocaleLowerCase('ru')
  const groups = getGroupedPermissions(permissions)
    .sort((a, b) => initialOrder.indexOf(a.groupId) - initialOrder.indexOf(b.groupId))
    .map(group => {
      const visible = group.permissions.filter(item =>
        isPermissionVisible(item, permissions, getValue),
      )
      const enabled = visible.filter(item => active(item.id)).length
      const matchesGroup = group.groupName?.toLocaleLowerCase('ru').includes(query)
      const rows = visible.filter(
        item =>
          (!onlyEnabled || active(item.id)) &&
          (!query ||
            matchesGroup ||
            `${item.description} ${item.name}`.toLocaleLowerCase('ru').includes(query)),
      )
      return { ...group, visible, enabled, rows }
    })
    .filter(group => group.rows.length)

  const update = (id: number, value: boolean | string | null) => {
    const next = currentPermissions.filter(item => item.id !== id)
    if (value !== null && value !== false && value !== '') next.push({ id, value })
    onPermissionChange(next)
  }

  return (
    <RoleSettingsSection
      className="role-permissions"
      title="Права доступа"
      description="Настройте, какие разделы и действия доступны сотрудникам с этой ролью."
    >
      <div className="role-toolbar">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          value={search}
          aria-label="Поиск прав доступа"
          placeholder="Найти раздел или разрешение"
          onChange={event => setSearch(event.target.value)}
        />
        <Segmented
          aria-label="Фильтр прав доступа"
          value={onlyEnabled ? 'enabled' : 'all'}
          onChange={value => setOnlyEnabled(value === 'enabled')}
          options={[
            { label: 'Все', value: 'all' },
            { label: 'Включённые', value: 'enabled' },
          ]}
        />
      </div>
      <div className="role-permission-groups">
        {groups.map(group => {
          const open =
            Boolean(query) ||
            (expanded[String(group.groupId)] ?? initiallyExpanded.has(group.groupId))
          const contentId = `role-permission-group-${group.groupId ?? 'general'}`
          const state = !group.enabled
            ? 'disabled'
            : group.enabled === group.visible.length
            ? 'enabled'
            : 'partial'
          const name = (group.groupName || '').toLowerCase()
          const Icon = name.includes('производ')
            ? ToolOutlined
            : name.includes('рекламац')
            ? AlertOutlined
            : name.includes('логист')
            ? CarOutlined
            : name.includes('отчет') || name.includes('отчёт')
            ? BarChartOutlined
            : name.includes('персонал')
            ? TeamOutlined
            : name.includes('администр')
            ? SettingOutlined
            : name.includes('снабжен')
            ? ShoppingCartOutlined
            : AppstoreOutlined
          return (
            <section className="role-permission-group" key={group.groupId ?? 'general'}>
              <button
                className="role-group-heading"
                type="button"
                aria-expanded={open}
                aria-controls={contentId}
                onClick={() =>
                  setExpanded(current => ({ ...current, [String(group.groupId)]: !open }))
                }
              >
                <Icon className="role-group-icon" />
                <span className="role-group-title">{group.groupName || 'Общие права'}</span>
                <span className={`role-access-state ${state}`}>
                  {state === 'disabled'
                    ? 'Отключено'
                    : state === 'enabled'
                    ? 'Включено'
                    : 'Частично'}
                </span>
                <span className="role-group-summary">
                  {group.enabled} из {group.visible.length}
                </span>
                <DownOutlined className={`role-group-arrow${open ? ' is-open' : ''}`} />
              </button>
              {open && (
                <div id={contentId} className="role-permission-rows">
                  {group.rows.map(permission => (
                    <div className="role-permission-row" key={permission.id}>
                      <label
                        id={`role-permission-label-${permission.id}`}
                        htmlFor={`role-permission-${permission.id}`}
                      >
                        {permission.description}
                      </label>
                      <div className="role-permission-control">
                        {permission.settings.type === 'checkbox' ? (
                          <Switch
                            id={`role-permission-${permission.id}`}
                            aria-labelledby={`role-permission-label-${permission.id}`}
                            checked={getValue(permission.id) === true}
                            onChange={checked => update(permission.id, checked)}
                          />
                        ) : (
                          <Select
                            id={`role-permission-${permission.id}`}
                            aria-labelledby={`role-permission-label-${permission.id}`}
                            allowClear
                            placeholder="Нет доступа"
                            value={getValue(permission.id) as string | null}
                            options={permission.settings.options}
                            onChange={value => update(permission.id, value ?? null)}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )
        })}
        {!groups.length && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={query ? 'Разрешения не найдены' : 'Нет включённых разрешений'}
          />
        )}
      </div>
    </RoleSettingsSection>
  )
}
